import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Empty,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  AppstoreOutlined,
  BarsOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  LockOutlined,
  MessageOutlined,
  PlusOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import ResponsiveDatePicker from "@/components/ResponsiveDatePicker";
import {
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_STEPS,
} from "./constants";
import {
  ActionBar,
  ActionGroup,
  AmountValue,
  BackButton,
  CardGrid,
  ContractorIdentity,
  ContractorQuoteCard,
  ContractorQuoteGrid,
  ContractorQuoteHeader,
  DetailCard,
  DetailField,
  DetailFieldGrid,
  DetailLabel,
  DetailValue,
  DescriptionValue,
  EmptyNotes,
  Header,
  HeaderCopy,
  HeaderIdentity,
  HeaderTitle,
  LatestQuote,
  Page,
  QuoteHistory,
  QuoteMeta,
  QuoteRevision,
  RequestId,
  SectionHeading,
  StatusPill,
  StepConnector,
  StepDot,
  StepItem,
  StepLabel,
  Stepper,
  StepTrack,
  Timeline,
  TimelineItem,
  TimelineMeta,
  TimelineText,
  TimelineTime,
  TimelineAuthor,
  WorkOrderLink,
} from "./ContractDetail.styles";
import ContractNoteModal from "./ContractNoteModal";

const getLabel = (status) =>
  CONTRACT_STATUS_LABELS[status] || status || "-";

const getTone = (status) => (CONTRACT_STATUS_COLORS[status] ? status : "closed");

const getInitials = (name) => {
  const value = String(name || "?").trim();
  if (!value) return "?";
  const parts = value.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : value.slice(0, 2))
    .toUpperCase();
};

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD HH:mm") : "-";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "-";
};

const formatQuoteDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "-";

const AttachmentItem = ({ attachment }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const preview = async () => {
    setPreviewLoading(true);
    try {
      const response = await fetch(attachment.url);
      if (!response.ok) throw new Error("Preview failed");
      const objectUrl = URL.createObjectURL(await response.blob());
      setPreviewUrl(objectUrl);
      setPreviewOpen(true);
    } catch {
      window.open(attachment.url, "_blank", "noopener,noreferrer");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, marginRight: 12 }}>
      <Button type="link" size="small" icon={<EyeOutlined />} loading={previewLoading} onClick={preview}>
        {attachment.original_name || "Preview attachment"}
      </Button>
      <Button
        type="text"
        size="small"
        icon={<DownloadOutlined />}
        href={attachment.url}
        download={attachment.original_name}
        title="Download attachment"
        aria-label={`Download ${attachment.original_name || "attachment"}`}
      />
      <Modal
        title={attachment.original_name || "Attachment preview"}
        open={previewOpen}
        footer={null}
        width="min(960px, 92vw)"
        destroyOnClose
        onCancel={() => setPreviewOpen(false)}
      >
        {previewUrl ? (
          <iframe
            title={attachment.original_name || "Attachment preview"}
            src={previewUrl}
            style={{ width: "100%", height: "70vh", border: 0 }}
          />
        ) : null}
      </Modal>
    </span>
  );
};

const renderAttachment = (attachment) => (
  <AttachmentItem key={attachment.id} attachment={attachment} />
);

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.status ||
  error?.response?.data?.detail ||
  error?.response?.data?.assignTo ||
  Object.values(error?.response?.data || {})[0] ||
  fallback;

export default function ContractDetail({
  requestId,
  listPath,
  loadRequest,
  role,
  addNote,
  canAddNote,
  approve,
  reject,
  recall,
  submitQuote,
  complete,
  closeContract,
  award,
  createWorkorder,
  getWorkorderPath,
}) {
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState(null);
  const [quoteStartTime, setQuoteStartTime] = useState(null);
  const [quoteEndTime, setQuoteEndTime] = useState(null);
  const [quoteDescription, setQuoteDescription] = useState("");
  const [quoteFiles, setQuoteFiles] = useState([]);
  const [initialQuoteAttachmentIds, setInitialQuoteAttachmentIds] = useState([]);
  const [quoteView, setQuoteView] = useState("cards");

  const loadRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await loadRequest(requestId);
      setRecord(response?.data ?? response);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to load contract"));
    } finally {
      setLoading(false);
    }
  }, [loadRequest, requestId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const currentStatus = record?.status || "submitted";
  const isContractRole = role === "contract";
  const isFacilityRole = role === "facility" || role === "portfolio";
  const availableActions = record?.available_actions || [];
  const noteEnabled = Boolean(canAddNote && availableActions.includes("add_note"));
  const canSubmitQuote = Boolean(
    isContractRole && submitQuote && availableActions.includes("submit_quote")
  );
  const canApprove = Boolean(
    isContractRole && approve && availableActions.includes("approve")
  );
  const canReject = Boolean(
    isContractRole && reject && availableActions.includes("reject")
  );
  const canRecall = Boolean(
    isContractRole && recall && availableActions.includes("recall")
  );
  const canComplete = Boolean(
    isContractRole && complete && availableActions.includes("complete")
  );
  const canClose = Boolean(
    isFacilityRole && closeContract && availableActions.includes("close")
  );
  const canCreateWorkorder = Boolean(
    isFacilityRole && createWorkorder && availableActions.includes("create_workorder")
  );

  const notes = useMemo(
    () => [...(record?.notes || [])].sort((a, b) => new Date(b.created_date) - new Date(a.created_date)),
    [record?.notes]
  );

  const contractorQuoteGroups = useMemo(() => {
    const groups = new Map();
    (record?.quotes || []).forEach((quote) => {
      const key = String(quote.contractor);
      if (!groups.has(key)) {
        groups.set(key, {
          contractorId: quote.contractor,
          contractorName: quote.contractor_name || "Unknown contractor",
          contractorEmail: quote.contractor_email,
          contractorLogo: quote.contractor_logo,
          quotes: [],
        });
      }
      groups.get(key).quotes.push(quote);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        quotes: group.quotes.sort(
          (a, b) => new Date(b.created_date) - new Date(a.created_date)
        ),
      }))
      .sort((a, b) => new Date(b.quotes[0].created_date) - new Date(a.quotes[0].created_date));
  }, [record?.quotes]);

  const currentQuote = useMemo(
    () => (isContractRole && record?.quotes?.length ? record.quotes[0] : null),
    [isContractRole, record?.quotes]
  );

  const openQuoteModal = () => {
    const attachments = currentQuote?.attachments || [];
    setQuoteAmount(currentQuote?.amount ?? null);
    setQuoteStartTime(currentQuote?.start_time ? moment(currentQuote.start_time) : null);
    setQuoteEndTime(currentQuote?.end_time ? moment(currentQuote.end_time) : null);
    setQuoteDescription(currentQuote?.description || "");
    setInitialQuoteAttachmentIds(attachments.map((item) => item.id));
    setQuoteFiles(attachments.map((item) => ({
      uid: `existing-${item.id}`,
      id: item.id,
      name: item.original_name || "Attachment",
      status: "done",
      url: item.url,
    })));
    setQuoteModalOpen(true);
  };

  const submitNote = async (content, files) => {
    if (!content.replace(/<[^>]*>/g, "").trim() && !files.length) {
      message.error("Please enter a note");
      return;
    }
    setSubmittingNote(true);
    try {
      const body = new FormData();
      body.append("content", content || "<p>Attachment</p>");
      files.forEach((file) => body.append("attachments", file));
      await addNote(requestId, body);
      setNoteModalOpen(false);
      await loadRecord();
      message.success("Note added successfully");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to add note"));
    } finally {
      setSubmittingNote(false);
    }
  };

  const submitQuoteRequest = async () => {
    if (quoteAmount === null || quoteAmount === undefined) {
      message.error("Please enter a quote amount");
      return;
    }
    if (!quoteStartTime || !quoteEndTime) {
      message.error("Please select a start time and end time");
      return;
    }
    if (quoteEndTime.isBefore(quoteStartTime)) {
      message.error("End time must be after start time");
      return;
    }
    setChangingStatus(true);
    try {
      const body = new FormData();
      body.append("amount", quoteAmount);
      body.append("start_time", quoteStartTime.format("YYYY-MM-DD"));
      body.append("end_time", quoteEndTime.format("YYYY-MM-DD"));
      body.append("description", quoteDescription);
      quoteFiles
        .filter((item) => item.originFileObj)
        .forEach((item) => body.append("attachments", item.originFileObj));
      const retainedAttachmentIds = new Set(
        quoteFiles.filter((item) => item.id).map((item) => item.id)
      );
      initialQuoteAttachmentIds
        .filter((id) => !retainedAttachmentIds.has(id))
        .forEach((id) => body.append("remove_attachment_ids", id));
      await submitQuote(record.id, body);
      setQuoteModalOpen(false);
      setQuoteAmount(null);
      setQuoteStartTime(null);
      setQuoteEndTime(null);
      setQuoteDescription("");
      setQuoteFiles([]);
      setInitialQuoteAttachmentIds([]);
      await loadRecord();
      message.success(currentQuote ? "Quote updated" : "Quote submitted");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to submit quote"));
    } finally {
      setChangingStatus(false);
    }
  };

  const changeStatus = async (action, successText) => {
    if (!action || !record) return;
    setChangingStatus(true);
    try {
      await action(record.id);
      await loadRecord();
      message.success(successText);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to update contract status"));
    } finally {
      setChangingStatus(false);
    }
  };

  const currentIndex = Math.max(
    0,
    CONTRACT_STEPS.findIndex((step) => step.key === currentStatus)
  );

  const actionButtons = (
    <ActionGroup>
      {noteEnabled ? (
        <Button icon={<MessageOutlined />} onClick={() => setNoteModalOpen(true)}>
          Add Note
        </Button>
      ) : null}
      {canSubmitQuote ? (
        <Button type="primary" icon={<DollarOutlined />} onClick={openQuoteModal}>
          {currentQuote ? "Edit Quote" : "Submit Quote"}
        </Button>
      ) : null}
      {canApprove ? (
        <Button
          type="primary"
          icon={<ToolOutlined />}
          onClick={() => changeStatus(approve, "Award accepted")}
          loading={changingStatus}
        >
          Approve
        </Button>
      ) : null}
      {canReject ? (
        <Button danger icon={<CloseCircleOutlined />} onClick={() => changeStatus(reject, "Award rejected")} loading={changingStatus}>
          Reject
        </Button>
      ) : null}
      {canRecall ? (
        <Button danger icon={<CloseCircleOutlined />} onClick={() => changeStatus(recall, "Contract recalled to bidding")} loading={changingStatus}>
          Recall
        </Button>
      ) : null}
      {canComplete ? (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => changeStatus(complete, "Contract completed")}
          loading={changingStatus}
        >
          Complete
        </Button>
      ) : null}
      {canClose ? (
        <Button
          danger
          icon={<LockOutlined />}
          onClick={() => changeStatus(closeContract, "Contract closed")}
          loading={changingStatus}
        >
          Close
        </Button>
      ) : null}
      {canCreateWorkorder ? (
        <Button icon={<ToolOutlined />} onClick={() => createWorkorder(record)}>
          Create Work Order
        </Button>
      ) : null}
    </ActionGroup>
  );

  return (
    <Page>
      <Header>
        <HeaderIdentity>
          <BackButton icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)} aria-label="Back" />
          <HeaderCopy>
            <HeaderTitle level={4}>Contract Request {record ? `#${record.id}` : ""}</HeaderTitle>
            {record ? <RequestId>{record.title}</RequestId> : null}
          </HeaderCopy>
          {record ? (
            <StatusPill $status={getTone(currentStatus)}>{getLabel(currentStatus)}</StatusPill>
          ) : null}
        </HeaderIdentity>
      </Header>

      <Spin spinning={loading}>
        {record ? (
          <>
            <Stepper aria-label="Contract progress">
              <StepTrack>
                {CONTRACT_STEPS.map((step, index) => {
                  const done = index < currentIndex;
                  const active = index === currentIndex;
                  return (
                    <StepItem key={step.key} $active={active} $done={done}>
                      <StepDot $active={active} $done={done}>
                        {done ? <CheckOutlined /> : index === 0 ? <FileTextOutlined /> : index === CONTRACT_STEPS.length - 1 ? <LockOutlined /> : <ToolOutlined />}
                      </StepDot>
                      <StepLabel $active={active} $done={done}>
                        {step.label}
                      </StepLabel>
                      {index < CONTRACT_STEPS.length - 1 ? (
                        <StepConnector $done={done} $active={active} />
                      ) : null}
                    </StepItem>
                  );
                })}
              </StepTrack>
            </Stepper>
            <ActionBar>{actionButtons}</ActionBar>

            <CardGrid>
              <DetailCard>
                <SectionHeading><FileTextOutlined /> Contract Information</SectionHeading>
                <DetailField>
                  <DetailLabel>Title / Issue</DetailLabel>
                  <DetailValue>{record.title || "-"}</DetailValue>
                </DetailField>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Facility</DetailLabel>
                    <DetailValue>{record.facility_name || record.facility || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Company</DetailLabel>
                    <DetailValue>{record.company_name || "-"}</DetailValue>
                  </DetailField>
                </DetailFieldGrid>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Work Order</DetailLabel>
                    <DetailValue>
                      {record.workorder_id ? (
                        getWorkorderPath ? (
                          <WorkOrderLink
                            type="link"
                            onClick={() => navigate(getWorkorderPath(record))}
                          >
                            {record.workorder_number || `Work Order #${record.workorder_id}`}
                          </WorkOrderLink>
                        ) : (
                          record.workorder_number || `Work Order #${record.workorder_id}`
                        )
                      ) : "-"}
                    </DetailValue>
                  </DetailField>
                  {!isContractRole ? (
                    <DetailField>
                      <DetailLabel>Tenant Request</DetailLabel>
                      <DetailValue>{record.service_request_id ? `#${record.service_request_id}` : "-"}</DetailValue>
                    </DetailField>
                  ) : null}
                </DetailFieldGrid>
              </DetailCard>

              <DetailCard>
                <SectionHeading><ToolOutlined /> Assignment & Dates</SectionHeading>
                <DetailField>
                  <DetailLabel>Assignee</DetailLabel>
                  {record.assignTo_name ? (
                    <Space size={8}>
                      <Avatar size={28} src={record.assignTo_logo}>{getInitials(record.assignTo_name)}</Avatar>
                      <span><span style={{ display: "block" }}>{record.assignTo_name}</span>{record.assignTo_email ? <small>{record.assignTo_email}</small> : null}</span>
                    </Space>
                  ) : <DetailValue>Unclaimed</DetailValue>}
                </DetailField>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Equipment</DetailLabel>
                    <DetailValue>{record.equipment_name || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Priority</DetailLabel>
                    <DetailValue $priority={record.priority}>{record.priority || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Work Due Date</DetailLabel>
                    <DetailValue>{record.due_date ? formatDate(record.due_date) : "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Bid Due Date</DetailLabel>
                    <DetailValue>{record.bid_due_date ? formatDate(record.bid_due_date) : "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Created</DetailLabel>
                    <DetailValue>{formatDate(record.created_date)}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Last Updated</DetailLabel>
                    <DetailValue>{formatDate(record.modified_date || record.created_date)}</DetailValue>
                  </DetailField>
                </DetailFieldGrid>
              </DetailCard>

              <DetailCard $full>
                <SectionHeading><FileTextOutlined /> Description</SectionHeading>
                <DescriptionValue>{record.description || "No description provided."}</DescriptionValue>
              </DetailCard>

              {record.total_cost !== null && record.total_cost !== undefined ? (
                <DetailCard $full>
                  <SectionHeading><FileTextOutlined /> Cost</SectionHeading>
                  <DetailField>
                    <DetailLabel>Total Cost</DetailLabel>
                    <AmountValue>{formatMoney(record.total_cost)}</AmountValue>
                  </DetailField>
                </DetailCard>
              ) : null}

              <DetailCard $full>
                <SectionHeading $spread>
                  <span><DollarOutlined /> {isFacilityRole ? "Contractor Quotes" : "Quote"}</span>
                  {isFacilityRole && (record.quotes || []).length ? (
                    <Segmented
                      size="small"
                      value={quoteView}
                      onChange={setQuoteView}
                      options={[
                        { value: "cards", icon: <AppstoreOutlined />, label: "Cards" },
                        { value: "list", icon: <BarsOutlined />, label: "List" },
                      ]}
                    />
                  ) : null}
                </SectionHeading>
                {(record.quotes || []).length ? (
                  isFacilityRole && quoteView === "cards" ? (
                    <>
                      <QuoteMeta style={{ marginBottom: 14 }}>
                        One card per contractor. Awarding uses that contractor&apos;s latest quote.
                      </QuoteMeta>
                      <ContractorQuoteGrid>
                        {contractorQuoteGroups.map((group) => {
                          const latestQuote = group.quotes[0];
                          const previousQuotes = group.quotes.slice(1);
                          const isAwarded = String(record.assignTo) === String(group.contractorId);
                          return (
                            <ContractorQuoteCard key={group.contractorId} $awarded={isAwarded}>
                              <ContractorQuoteHeader>
                                <ContractorIdentity>
                                  <Avatar size={40} src={group.contractorLogo}>{getInitials(group.contractorName)}</Avatar>
                                  <div>
                                    <strong>{group.contractorName}</strong>
                                    {group.contractorEmail ? <QuoteMeta>{group.contractorEmail}</QuoteMeta> : null}
                                    <QuoteMeta>
                                      {group.quotes.length} {group.quotes.length === 1 ? "quote" : "quotes"}
                                      {isAwarded ? " · Awarded contractor" : ""}
                                    </QuoteMeta>
                                  </div>
                                </ContractorIdentity>
                                {availableActions.includes("award") && award ? (
                                  <Button
                                    type="primary"
                                    size="small"
                                    loading={changingStatus}
                                    onClick={() => Modal.confirm({
                                      title: `Award contract to ${group.contractorName}?`,
                                      content: `The latest quote of ${formatMoney(latestQuote.amount)} will be selected.`,
                                      okText: "Award Contractor",
                                      onOk: () => changeStatus(
                                        () => award(record.id, group.contractorId),
                                        `Contract awarded to ${group.contractorName}`
                                      ),
                                    })}
                                  >
                                    Award Contractor
                                  </Button>
                                ) : null}
                              </ContractorQuoteHeader>
                              <LatestQuote $awarded={isAwarded}>
                                <QuoteMeta>Latest quote - {formatDate(latestQuote.created_date)}</QuoteMeta>
                                <AmountValue>{formatMoney(latestQuote.amount)}</AmountValue>
                                {latestQuote.start_time ? <QuoteMeta>Schedule: {formatQuoteDate(latestQuote.start_time)} - {formatQuoteDate(latestQuote.end_time)}</QuoteMeta> : null}
                                {latestQuote.description ? <p style={{ margin: "8px 0 0" }}>{latestQuote.description}</p> : null}
                                {(latestQuote.attachments || []).map(renderAttachment)}
                              </LatestQuote>
                              {previousQuotes.length ? (
                                <QuoteHistory>
                                  <summary>View {previousQuotes.length} previous {previousQuotes.length === 1 ? "quote" : "quotes"}</summary>
                                  {previousQuotes.map((quote) => (
                                    <QuoteRevision key={quote.id}>
                                      <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
                                        <QuoteMeta>{formatDate(quote.created_date)}</QuoteMeta>
                                        <strong>{formatMoney(quote.amount)}</strong>
                                      </Space>
                                      {quote.start_time ? <QuoteMeta>Schedule: {formatQuoteDate(quote.start_time)} - {formatQuoteDate(quote.end_time)}</QuoteMeta> : null}
                                      {quote.description ? <p style={{ margin: "6px 0 0" }}>{quote.description}</p> : null}
                                      {(quote.attachments || []).map(renderAttachment)}
                                    </QuoteRevision>
                                  ))}
                                </QuoteHistory>
                              ) : null}
                            </ContractorQuoteCard>
                          );
                        })}
                      </ContractorQuoteGrid>
                    </>
                  ) : isFacilityRole && quoteView === "list" ? (
                    <Table
                      rowKey="id"
                      size="small"
                      bordered
                      pagination={false}
                      scroll={{ x: 900 }}
                      dataSource={record.quotes}
                      columns={[
                        {
                          title: "Contractor",
                          key: "contractor",
                          width: 220,
                          render: (_, quote) => (
                            <div>
                              <strong>{quote.contractor_name || "Unknown contractor"}</strong>
                              {quote.contractor_email ? <QuoteMeta>{quote.contractor_email}</QuoteMeta> : null}
                            </div>
                          ),
                        },
                        {
                          title: "Submitted",
                          dataIndex: "created_date",
                          key: "created_date",
                          width: 160,
                          render: formatDate,
                        },
                        {
                          title: "Amount",
                          dataIndex: "amount",
                          key: "amount",
                          width: 120,
                          render: (amount) => <AmountValue>{formatMoney(amount)}</AmountValue>,
                        },
                        {
                          title: "Schedule",
                          key: "schedule",
                          width: 190,
                          render: (_, quote) => (
                            quote.start_time
                              ? `${formatQuoteDate(quote.start_time)} - ${formatQuoteDate(quote.end_time)}`
                              : "-"
                          ),
                        },
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: 260,
                          render: (description) => (
                            <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                              {description || "-"}
                            </span>
                          ),
                        },
                        {
                          title: "Attachments",
                          key: "attachments",
                          width: 180,
                          render: (_, quote) => (quote.attachments || []).length ? (
                            <Space wrap size={[4, 4]}>
                              {quote.attachments.map(renderAttachment)}
                            </Space>
                          ) : "-",
                        },
                      ]}
                    />
                  ) : (
                    <Space direction="vertical" style={{ width: "100%" }} size={12}>
                      {record.quotes.map((quote) => (
                        <div key={quote.id} style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 12 }}>
                          <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
                            <div>
                              <strong>{quote.contractor_name}</strong>
                              {quote.contractor_email ? <small style={{ marginLeft: 8 }}>{quote.contractor_email}</small> : null}
                              <span style={{ marginLeft: 12 }}>{formatDate(quote.created_date)}</span>
                              {quote.start_time ? <small style={{ display: "block", marginTop: 4 }}>Schedule: {formatQuoteDate(quote.start_time)} - {formatQuoteDate(quote.end_time)}</small> : null}
                            </div>
                            <AmountValue>{formatMoney(quote.amount)}</AmountValue>
                          </Space>
                          {quote.description ? <p style={{ margin: "8px 0" }}>{quote.description}</p> : null}
                          {(quote.attachments || []).map(renderAttachment)}
                        </div>
                      ))}
                    </Space>
                  )
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No quotes submitted" />
                )}
              </DetailCard>

              <DetailCard $full>
                <SectionHeading $spread>
                  <span><MessageOutlined /> Activity & Notes</span>
                  {noteEnabled ? (
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setNoteModalOpen(true)}>
                      Add Note
                    </Button>
                  ) : null}
                </SectionHeading>
                {notes.length ? (
                  <Timeline>
                    {notes.map((item) => {
                      const author = item.createdby_name || "Unknown";
                      const authorEmail = item.createdby_contract_company_email;
                      return (
                        <TimelineItem key={item.id || `${item.created_date}-${author}`}>
                          <Avatar size={36} src={item.createdby_contract_company_logo}>{getInitials(author)}</Avatar>
                          <div>
                            <TimelineMeta>
                              <TimelineAuthor>{author}</TimelineAuthor>
                              <TimelineTime>{formatDate(item.created_date)}</TimelineTime>
                            </TimelineMeta>
                            {authorEmail ? <QuoteMeta>{authorEmail}</QuoteMeta> : null}
                            <TimelineText dangerouslySetInnerHTML={{ __html: item.content || item.text || "" }} />
                            {(item.attachments || []).map(renderAttachment)}
                          </div>
                        </TimelineItem>
                      );
                    })}
                  </Timeline>
                ) : (
                  <EmptyNotes image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes yet" />
                )}
              </DetailCard>
            </CardGrid>
          </>
        ) : null}
      </Spin>

      <ContractNoteModal
        open={noteModalOpen}
        loading={submittingNote}
        onCancel={() => setNoteModalOpen(false)}
        onSubmit={submitNote}
      />
      <Modal
        title={currentQuote ? "Edit Quote" : "Submit Quote"}
        open={quoteModalOpen}
        onCancel={() => setQuoteModalOpen(false)}
        onOk={submitQuoteRequest}
        confirmLoading={changingStatus}
        okText={currentQuote ? "Save Changes" : "Submit Quote"}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 6 }}>Project Period <span style={{ color: "#ff4d4f" }}>*</span></div>
            <ResponsiveDatePicker.RangePicker
              value={[quoteStartTime, quoteEndTime]}
              onChange={(values) => {
                setQuoteStartTime(values?.[0] || null);
                setQuoteEndTime(values?.[1] || null);
              }}
              format="YYYY-MM-DD"
              placeholder={["Start date", "End date"]}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>Amount <span style={{ color: "#ff4d4f" }}>*</span></div>
          <InputNumber
            min={0}
            precision={2}
            prefix="$"
            value={quoteAmount}
            onChange={setQuoteAmount}
            placeholder="Quote amount"
            style={{ width: "100%" }}
          />
          </div>
          <Input.TextArea
            rows={4}
            value={quoteDescription}
            onChange={(event) => setQuoteDescription(event.target.value)}
            placeholder="Notes (optional)"
          />
          <Upload
            multiple
            fileList={quoteFiles}
            beforeUpload={() => false}
            onChange={({ fileList }) => setQuoteFiles(fileList)}
          >
            <Button icon={<PlusOutlined />}>Add Attachments</Button>
          </Upload>
        </Space>
      </Modal>
    </Page>
  );
}
